import React, {useState} from "react";
import {Document, DocumentReadStatusDto, DocumentRestControllerApiFactory} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {Box, Button, Chip, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import {PersonAvatar, tableContainerSx, tableSx} from "../../../../components/kit";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {dateToString} from "../../../../components/utils/dates";
import {ReviewStatusChip} from "./review_status";
import FormFile from "../../../../components/form/form_inputs/form_file";
import FormAutocompleteMulti from "../../../../components/form/form_inputs/form_autocomplete_multi";
import {types} from "../../../../components/form/types";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";

export const DocumentEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(DocumentRestControllerApiFactory);
    // Quién ha leído la edición vigente y a quién le falta (#13676).
    const [lecturas, setLecturas] = useState<DocumentReadStatusDto>();


    const form = useForm({
        fetcher: (id) => ctrl.getDocument(id),
        updater: (bean) => ctrl.saveDocument(bean),
        remover: (id) => ctrl.deleteOrDismissDocument(id),
        factory: () => {
            return { } as Document;
        },
        postLoad: (bean: Document) => {
            if (bean.id) ctrl.readsDocument(bean.id).then(resp => setLecturas(resp.data));
        },
    } as UseFormProps<Document>);

    return (
        <>
            <FormContent form={form} title={"Ficha de documento"}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormInput field={"title"} type={"text"} label={"Título"} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormInput field={"editionNumber"} type={"number"} label={"Edición"}
                                   helperText={"Sube sola al sustituir el archivo; vuelve a pedir la lectura"}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormInput field={"editionDate"} type={"date"} label={"Fecha de la edición"}/>
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormAutocomplete field={"topic"} label={"Tema"} type={types.documentTopics}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormAutocomplete field={"reviewOwner"} label={"Responsable de revisarlo"} type={types.people}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormInput field={"lastReviewDate"} type={"date"} label={"Última revisión"}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormInput field={"reviewMonths"} type={"number"} label={"Revisar cada (meses)"}
                                   helperText={"Vacío: no caduca"}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {/* Revisión con caducidad (#13729): validar sin cambios no es edición nueva. */}
                        <Stack direction={"row"} spacing={2} alignItems={"center"} sx={{height: "100%"}}>
                            <ReviewStatusChip status={form.value("reviewStatus")}/>
                            {form.value("nextReviewDate") &&
                                <Typography variant={"body2"}>Próxima: {dateToString(form.value("nextReviewDate"))}</Typography>}
                            {form.hasId() && form.value("reviewMonths") &&
                                <Button size={"small"} variant={"outlined"}
                                        onClick={() => apiManager.execute(ctrl.reviewedDocument(form.getIdOrErr()))
                                            .then((doc) => form.setter(doc))}>
                                    Revisado, sigue vigente
                                </Button>}
                        </Stack>
                    </Grid>
                </Grid>

                <FormAutocompleteMulti field={"units"} label={"Dirigido a (unidades)"} type={types.organizationUnits}
                                       helpText={"Vacío, y sin roles ni accesos: toda la plantilla. Con unidades: sus personas y las de sus subunidades"}/>

                <FormAutocompleteMulti field={"roles"} label={"Dirigido a (roles del sistema de gestión)"} type={types.managementRoles}
                                       helpText={"Sus titulares y suplentes vigentes, además de las unidades"}/>

                <FormAutocompleteMulti field={"accesses"} label={"Dirigido a (accesos a la aplicación)"} type={types.documentAccesses}
                                       helpText={"Quien tiene ese permiso, por ejemplo RR. HH. Administración no lo hereda"}/>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormFile
                            field={"attachment"}
                            label={"Archivo"}
                            onDownload={() =>
                                apiManager.executeRaw(ctrl.viewDocument(form.getIdOrErr()))
                            }
                        />
                    </Grid>
                </Grid>
                {lecturas && (
                    <Box sx={{mt: 3}}>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 0.5}}>
                            <Typography variant="subtitle1" fontWeight={700}>
                                Lectura de la edición {lecturas.edition}
                            </Typography>
                            <Chip size="small" color="success" label={`Leído por ${(lecturas.read || []).length}`}/>
                            <Chip size="small" color={(lecturas.pending || []).length ? "warning" : "default"}
                                  label={`Falta ${(lecturas.pending || []).length}`}/>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1.5}}>
                            Sobre sus destinatarios de hoy.
                        </Typography>
                        <TableContainer sx={tableContainerSx}>
                            <Table size={"small"} sx={tableSx}>
                                <TableHead>
                                    <TableRow><TableCell>Persona</TableCell><TableCell>Leído el</TableCell></TableRow>
                                </TableHead>
                                <TableBody>
                                    {(lecturas.read || []).map(r => (
                                        <TableRow key={"r" + r.personId}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <PersonAvatar name={r.name} size={28}/>
                                                    <Typography variant="body2">{r.name}</Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{fontVariantNumeric: "tabular-nums"}}>
                                                {(r.readAt || "").replace("T", " ").substring(0, 16)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(lecturas.pending || []).map(r => (
                                        <TableRow key={"p" + r.personId}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <PersonAvatar name={r.name} size={28}/>
                                                    <Typography variant="body2">{r.name}</Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell><Chip size="small" color="warning" label="Pendiente"/></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </FormContent>
        </>
    );
};